import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'

/**
 * FAQ Editor Controller
 *
 * Stimulus controller for mounting the FaqEditor Vue component in a slideout panel.
 * Handles FAQ CRUD operations and AI generation for accordion/FAQ blocks.
 */
export default class extends Controller {
  static targets = ['mount']

  static values = {
    websiteId: String,
    pageId: String,
    blockId: String,
    items: String, // Base64-encoded JSON array of FAQ items
    fieldName: String, // The field name in block content (e.g., 'items' or 'faqs')
    schema: String, // Base64-encoded JSON schema for items
    csrf: String
  }

  connect() {
    this.vueApp = null
    this.itemsData = []
    this.schemaData = {}

    // Decode items from base64
    if (this.itemsValue) {
      try {
        this.itemsData = JSON.parse(decodeURIComponent(atob(this.itemsValue)))
      } catch (e) {
        this.itemsData = []
      }
    }

    // Decode schema from base64
    if (this.schemaValue) {
      try {
        this.schemaData = JSON.parse(decodeURIComponent(atob(this.schemaValue)))
      } catch (e) {
        this.schemaData = {}
      }
    }

    // Auto-mount if mount target exists
    if (this.hasMountTarget) {
      this.mountVue()
    }
  }

  disconnect() {
    this.unmountVue()
  }

  async mountVue() {
    if (this.vueApp || !this.hasMountTarget) return

    // Dynamic import of the Vue component
    const { default: FaqEditor } = await import('~/javascript/components/admin/page_editor/FaqEditor.vue')

    // Pass callback props for Vue -> Stimulus communication
    const props = {
      blockId: this.blockIdValue,
      websiteId: this.websiteIdValue,
      pageId: this.pageIdValue,
      initialItems: this.itemsData,
      itemSchema: this.schemaData,
      fieldName: this.fieldNameValue || 'items',
      csrfToken: this.csrfValue || document.querySelector('meta[name="csrf-token"]')?.content,
      // Callback props for event communication
      onUpdate: (data) => this.handleFaqUpdate(data),
      onClose: () => this.handleClose()
    }

    this.vueApp = createApp(FaqEditor, props)
    this.vueApp.mount(this.mountTarget)
  }

  unmountVue() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  handleFaqUpdate(data) {
    // Update local data
    if (data.items) {
      this.itemsData = data.items
    }

    // Dispatch custom event for parent components to sync
    this.dispatch('faqsUpdated', {
      detail: {
        blockId: this.blockIdValue,
        items: data.items,
        renderedHtml: data.renderedHtml
      }
    })

    // Refresh the preview iframe if available
    this.refreshPreview(data.renderedHtml)

    // Dispatch a plain DOM event for non-Stimulus listeners (e.g., legacy toolbar code)
    this.element.dispatchEvent(new CustomEvent('faq-editor:saved', {
      bubbles: true,
      detail: {
        blockId: this.blockIdValue,
        items: data.items,
        renderedHtml: data.renderedHtml
      }
    }))
  }

  handleClose() {
    // Dispatch close event for the slideout panel to close
    this.dispatch('close')
  }

  refreshPreview(renderedHtml) {
    // Find the preview iframe
    const previewIframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!previewIframe || !previewIframe.contentDocument) return

    // Find the block in the preview and update its HTML
    const blockSelector = `[data-block-id="${this.blockIdValue}"]`
    const blockInPreview = previewIframe.contentDocument.querySelector(blockSelector)

    if (blockInPreview && renderedHtml) {
      // Create a temporary container to parse the new HTML
      const temp = document.createElement('div')
      temp.innerHTML = renderedHtml

      // Get the new section element
      const newBlock = temp.firstElementChild
      if (newBlock) {
        blockInPreview.replaceWith(newBlock)
      }
    }
  }

  // Public method to update items externally
  updateItems(items) {
    this.itemsData = items

    // Remount Vue with new data
    this.unmountVue()
    this.mountVue()
  }
}
